import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Badge } from '@/components/ui/Badge'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { DEMO_EVIDENCE, DEMO_SEGMENTS } from '@/lib/demo/data'
import { cn } from '@/lib/utils'

const PAGE_SIZE = 10

interface ColumnRow {
  column_name: string
  category: string
  sample_values: string  // JSON string
}

interface ApiData {
  upload_id: string
  columns: ColumnRow[]
  total: number
}

const CATEGORY_COLORS: Record<string, string> = {
  respondent_id: 'bg-evidence-blue/10 text-evidence-blue border-evidence-blue/20',
  demographic: 'bg-purple-50 text-purple-700 border-purple-200',
  prediction: 'bg-human-teal/10 text-human-teal border-human-teal/20',
  open_ended: 'bg-signal-cyan/10 text-signal-cyan border-signal-cyan/20',
  high_frequency: 'bg-orange-50 text-orange-700 border-orange-200',
  numerical: 'bg-slate-50 text-slate-600 border-slate-200',
  outcome: 'bg-validation-green/10 text-validation-green border-validation-green/20',
  ignore: 'bg-soft-panel text-slate-400 border-border-gray',
}

export function DataPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const isDemo = !projectId || projectId === 'demo'

  // Demo state
  const [page, setPage] = useState(0)
  const [search, setSearch] = useState('')
  const [filterSegment, setFilterSegment] = useState('')

  // Real data state
  const [data, setData] = useState<ApiData | null>(null)
  const [loading, setLoading] = useState(!isDemo)
  const [filename, setFilename] = useState<string>('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isDemo) return
    const token = localStorage.getItem('echo_session')
    const authHeaders: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}

    // Fetch column data
    fetch(`/api/projects/${projectId}/data`, { headers: authHeaders })
      .then(r => r.ok ? r.json() as Promise<ApiData> : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(d => setData(d))
      .catch(err => setError(err instanceof Error ? err.message : 'Failed to load data'))
      .finally(() => setLoading(false))

    // Fetch upload list for filename
    fetch(`/api/projects/${projectId}/uploads`, { headers: authHeaders })
      .then(r => r.ok ? r.json() as Promise<Array<{ filename: string }>> : ([]))
      .then(ups => { if (ups[0]?.filename) setFilename(ups[0].filename) })
      .catch(() => {})
  }, [projectId, isDemo])

  // ── Demo view ──────────────────────────────────────────────────────────────
  if (isDemo) {
    const filtered = DEMO_EVIDENCE.filter(row => {
      const matchSearch = !search || (row.open_text ?? '').toLowerCase().includes(search.toLowerCase()) || (row.respondent_id ?? '').toLowerCase().includes(search.toLowerCase())
      const matchSegment = !filterSegment || row.segment === filterSegment
      return matchSearch && matchSegment
    })
    const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE)

    return (
      <div className="p-6 space-y-6 max-w-5xl pb-20 md:pb-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-medium text-deep-ink" style={{ letterSpacing: '-0.02em' }}>Data Explorer</h1>
            <p className="text-sm text-slate-500 mt-1 flex items-center gap-1.5">
              <Badge variant="demo" size="sm">Demo</Badge>
              {DEMO_EVIDENCE.length} rows · insurance-renewal-survey.csv
            </p>
          </div>
          <Button variant="outline" size="sm">Download CSV</Button>
        </div>

        <div className="flex flex-wrap gap-3">
          <input
            type="text"
            placeholder="Search open-end responses…"
            className="flex-1 min-w-48 border border-border-gray rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-human-teal/30"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0) }}
          />
          <select
            className="border border-border-gray rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-human-teal/30"
            value={filterSegment}
            onChange={e => { setFilterSegment(e.target.value); setPage(0) }}
          >
            <option value="">All segments</option>
            {DEMO_SEGMENTS.map(s => (
              <option key={s.segment_id} value={s.name}>{s.name}</option>
            ))}
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border-gray text-xs text-slate-400 text-left">
                <th className="pb-2 pr-3 font-medium">Row</th>
                <th className="pb-2 pr-3 font-medium">Respondent</th>
                <th className="pb-2 pr-3 font-medium">Segment</th>
                <th className="pb-2 pr-3 font-medium">Response</th>
                <th className="pb-2 font-medium">Open-ended text</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-gray">
              {paged.map((row, i) => (
                <tr key={i} className="hover:bg-soft-panel/50">
                  <td className="py-2.5 pr-3 text-slate-400 text-xs">{row.row_index}</td>
                  <td className="py-2.5 pr-3 font-mono text-xs text-slate-600">{row.respondent_id}</td>
                  <td className="py-2.5 pr-3">
                    <Badge variant="default" size="sm" className="text-xs">{row.segment}</Badge>
                  </td>
                  <td className="py-2.5 pr-3">
                    <span className={cn('text-xs font-medium', row.response === 'Message B' ? 'text-human-teal' : 'text-slate-500')}>{row.response}</span>
                  </td>
                  <td className="py-2.5 max-w-sm">
                    <p className="text-xs text-slate-600 truncate" title={row.open_text}>{row.open_text ?? '—'}</p>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-400">{filtered.length} rows · page {page + 1} of {Math.max(1, totalPages)}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>← Prev</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next →</Button>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-medium text-deep-ink mb-3">Evidence cards</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {paged.filter(r => r.open_text).slice(0, 4).map((row, i) => (
              <Card key={i} className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge variant="default" size="sm">{row.segment}</Badge>
                  <span className="text-xs text-slate-400">{row.source_file} · row {row.row_index}</span>
                </div>
                <p className="text-sm italic text-slate-600">"{row.open_text}"</p>
                <p className="text-xs text-slate-400">Response: <span className="font-medium text-deep-ink">{row.response}</span></p>
              </Card>
            ))}
          </div>
        </div>
      </div>
    )
  }

  // ── Real project: loading ──────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="p-6 max-w-5xl pb-20 md:pb-6">
        <h1 className="text-2xl font-medium text-deep-ink mb-6" style={{ letterSpacing: '-0.02em' }}>Data Explorer</h1>
        <Card className="text-center py-12">
          <p className="text-sm text-slate-400">Loading data…</p>
        </Card>
      </div>
    )
  }

  // ── Real project: no data ──────────────────────────────────────────────────
  if (!data || !data.columns || data.columns.length === 0) {
    return (
      <div className="p-6 max-w-5xl pb-20 md:pb-6">
        <h1 className="text-2xl font-medium text-deep-ink mb-6" style={{ letterSpacing: '-0.02em' }}>Data Explorer</h1>
        <Card className="text-center py-12 space-y-3">
          <p className="text-sm font-medium text-deep-ink">No data yet</p>
          <p className="text-xs text-slate-500">Upload a CSV to explore your study data.</p>
          {error && <p className="text-xs text-risk-rose">{error}</p>}
          <div className="pt-2">
            <Link to={`/app/projects/${projectId}/upload`}>
              <Button variant="primary" size="sm">Upload data →</Button>
            </Link>
          </div>
        </Card>
      </div>
    )
  }

  // ── Real project: column map ───────────────────────────────────────────────
  const colsByCategory = data.columns.reduce<Record<string, ColumnRow[]>>((acc, col) => {
    const cat = col.category || 'ignore'
    if (!acc[cat]) acc[cat] = []
    acc[cat].push(col)
    return acc
  }, {})

  const categoryOrder = ['respondent_id', 'outcome', 'prediction', 'demographic', 'open_ended', 'high_frequency', 'numerical', 'ignore']

  return (
    <div className="p-6 space-y-6 max-w-5xl pb-20 md:pb-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-medium text-deep-ink" style={{ letterSpacing: '-0.02em' }}>Data Explorer</h1>
          <p className="text-sm text-slate-500 mt-1">
            {data.total.toLocaleString()} rows · {data.columns.length} columns
            {filename && <> · <span className="font-mono text-xs">{filename}</span></>}
          </p>
        </div>
        <Link to={`/app/projects/${projectId}/upload`}>
          <Button variant="outline" size="sm">Upload new file</Button>
        </Link>
      </div>

      {/* Category summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { cat: 'demographic', label: 'Demographic' },
          { cat: 'open_ended', label: 'Open-ended' },
          { cat: 'outcome', label: 'Outcome' },
          { cat: 'prediction', label: 'Prediction' },
        ].map(({ cat, label }) => (
          <Card key={cat} className="bg-off-white border-0 text-center py-3">
            <p className="text-2xl font-medium text-deep-ink">{colsByCategory[cat]?.length ?? 0}</p>
            <p className="text-xs text-slate-500">{label} columns</p>
          </Card>
        ))}
      </div>

      {/* Column table */}
      <Card>
        <h3 className="text-sm font-medium text-deep-ink mb-4">Column map</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border-gray text-xs text-slate-400 text-left">
                <th className="pb-2 pr-4 font-medium">Column name</th>
                <th className="pb-2 pr-4 font-medium">Category</th>
                <th className="pb-2 font-medium">Sample values</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-gray">
              {categoryOrder.flatMap(cat =>
                (colsByCategory[cat] ?? []).map(col => {
                  let samples: string[] = []
                  try { samples = JSON.parse(col.sample_values) as string[] } catch { }
                  return (
                    <tr key={col.column_name} className="hover:bg-soft-panel/50">
                      <td className="py-2.5 pr-4 font-mono text-xs text-slate-600">{col.column_name}</td>
                      <td className="py-2.5 pr-4">
                        <span className={cn('text-xs px-2 py-0.5 rounded-full border font-medium', CATEGORY_COLORS[col.category] ?? 'bg-soft-panel text-slate-500 border-border-gray')}>
                          {col.category.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="py-2.5 max-w-xs">
                        <span className="text-xs text-slate-400">{samples.slice(0, 3).join(', ')}</span>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Open-ended evidence cards */}
      {colsByCategory['open_ended'] && colsByCategory['open_ended'].length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-deep-ink mb-3">Open-ended sample responses</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {colsByCategory['open_ended'].flatMap(col => {
              let samples: string[] = []
              try { samples = JSON.parse(col.sample_values) as string[] } catch { }
              return samples.slice(0, 2).map((text, i) => (
                <Card key={`${col.column_name}-${i}`} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="default" size="sm" className="text-xs">{col.column_name}</Badge>
                    <span className="text-xs text-slate-400">sample response</span>
                  </div>
                  <p className="text-sm italic text-slate-600">"{text}"</p>
                </Card>
              ))
            }).slice(0, 4)}
          </div>
        </div>
      )}
    </div>
  )
}
